import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@compiled/react';

import type { ExtensionHandler } from '@atlaskit/editor-common/extensions/extension-handler';
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import { token } from '@atlaskit/tokens';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import type { Node } from '@atlaskit/editor-prosemirror/model';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';
import { expValEquals } from '@confluence/feature-experiments';
import {
	EDITOR,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { TableOfContentsLoader } from '@confluence/table-of-contents/entry-points/TableOfContentsLoader.ts';
import { TeamCalendarMacroLoader } from '@confluence/team-calendar/entry-points/TeamCalendarMacroLoader.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { EXTENSION_KEY } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { hasConfiguredTeamCalendar } from '@confluence/fabric-extension-lib/entry-points/macroParamUtils.ts';
import { ContributorsMacroHandler } from '@confluence/contributors-macro/entry-points/ContributorsMacroRenderer.ts';
import { isMigratedMacroImplEnabled } from '@confluence/macro-utils/entry-points/evaluateMacroEnablement.ts';
import { ProfilePictureMacroHandler } from '@confluence/profile-macros/entry-points/ProfilePictureMacroRenderer.ts';
import { UserProfileMacroHandler } from '@confluence/profile-macros/entry-points/UserProfileMacroRenderer.ts';
import { RecentlyUpdatedMacroHandler } from '@confluence/recently-updated-macro/entry-points/RecentlyUpdatedMacroRenderer.ts';
import { ListLabelsMacroHandler } from '@confluence/listlabels-macro/entry-points/ListLabelsMacroRenderer.ts';
import { PopularLabelsMacroHandler } from '@confluence/popular-labels-macro/entry-points/PopularLabelsMacroRenderer.ts';
import { WidgetMacroHandler } from '@confluence/widget-macro/entry-points/WidgetMacroRenderer.ts';
import { ContentByLabelSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ContentByLabelSSRExtensionHandler.ts';
import { ContentReportTableMacroHandler } from '@confluence/content-report-table-macro/entry-points/ContentReportTableMacroRenderer.ts';
import { ViewXlsMacroHandler } from '@confluence/viewxls-macro/entry-points/ViewXlsMacroRenderer.ts';
import { PageIncludeSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/PageIncludeSSRExtensionHandler.ts';
import { AttachmentsMacroHandler } from '@confluence/attachments-macro/entry-points/AttachmentsMacroRenderer.ts';
import { BlogPostsSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/BlogPostsSSRExtensionHandler.ts';
import { DetailsSummaryExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/DetailsSummaryExtensionHandler.ts';
import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { ChangeHistoryMacroHandler } from '@confluence/change-history-macro/entry-points/ChangeHistoryMacroRenderer.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `${token('space.250')} ${token('space.250')}`,
	backgroundColor: token('color.background.neutral.subtle'),
});

const createEditorSSRExtensionHandlers = ({
	contentId,
	spaceKey,
	cloudId,
	userId,
}: {
	contentId: string;
	spaceKey: string;
	cloudId: string;
	userId: string | null;
}): { [key: string]: ExtensionHandler<any> } => {
	return {
		'com.atlassian.confluence.macro.core': (extension, doc) => {
			const { extensionKey } = extension;
			const attributes = getMacroAttributesFromADFNode(extension);
			const macroDefaultProps: ExtensionHandlerProps = {
				mode: EDITOR,
				node: extension,
				extensionKey,
				attributes,
				references: [],
				contentId,
				spaceKey,
			};

			if (
				extensionKey === EXTENSION_KEY.TEAM_CALENDARS &&
				hasConfiguredTeamCalendar(extension?.parameters?.macroParams) &&
				expValEquals('cc-engagement-team-calendar-macro-afm-migration', 'isEnabled', true)
			) {
				return (
					<TeamCalendarMacroLoader
						extensionNode={extension}
						macroDefaultProps={macroDefaultProps}
					/>
				);
			}

			if (extensionKey === 'toc') {
				const editorTransformer = new JSONTransformer();
				const adf = editorTransformer.encode(doc as Node);

				return (
					<Container>
						<TableOfContentsLoader
							isInEditMode
							adf={adf as ADNode}
							userId={userId || ''}
							cloudId={cloudId}
							macroDefaultProps={macroDefaultProps}
						/>
					</Container>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.CONTRIBUTORS &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.CONTRIBUTORS)
			) {
				return (
					<ContributorsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.RECENTLY_UPDATED &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.RECENTLY_UPDATED)
			) {
				return (
					<RecentlyUpdatedMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.POPULAR_LABELS &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.POPULAR_LABELS)
			) {
				return (
					<PopularLabelsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.LIST_LABELS &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.LIST_LABELS)
			) {
				return (
					<ListLabelsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.PROFILE_PICTURE &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.PROFILE_PICTURE)
			) {
				return (
					<ProfilePictureMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.DETAILS_SUMMARY &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.DETAILS_SUMMARY)
			) {
				return (
					<DetailsSummaryExtensionHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.PROFILE &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.PROFILE)
			) {
				return (
					<UserProfileMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.WIDGET &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.WIDGET)
			) {
				return (
					<WidgetMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.CONTENT_BY_LABEL &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.CONTENT_BY_LABEL)
			) {
				return (
					<ContentByLabelSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={createEditorSSRExtensionHandlers}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.INCLUDE &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.INCLUDE)
			) {
				return (
					<PageIncludeSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={createEditorSSRExtensionHandlers}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.BLOG_POSTS &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.BLOG_POSTS)
			) {
				return (
					<BlogPostsSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={createEditorSSRExtensionHandlers}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.CONTENT_REPORT_TABLE &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.CONTENT_REPORT_TABLE)
			) {
				return (
					<ContentReportTableMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.CHANGE_HISTORY &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.CHANGE_HISTORY)
			) {
				return (
					<ChangeHistoryMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}

			if (
				extensionKey === EXTENSION_KEY.ATTACHMENTS &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.ATTACHMENTS)
			) {
				return (
					<AttachmentsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			if (
				extensionKey === EXTENSION_KEY.VIEWXLS &&
				isMigratedMacroImplEnabled(EXTENSION_KEY.VIEWXLS)
			) {
				return (
					<ViewXlsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}

			// Unsupported (yet) extensions should default to existing provider-based handling
			// and rendering on a client side only.
			return null;
		},
	};
};

export const useEditorSSRExtensionHandlers = (
	contentId: string,
	spaceKey: string,
	extensionProviders: any[],
):
	| {
			[key: string]: ExtensionHandler<any>;
	  }
	| undefined => {
	const { cloudId, userId } = useSessionData();
	const [providersReady, setProvidersReady] = useState(false);

	const isTransition = useIsTransition();

	useEffect(() => {
		const providerPromises = extensionProviders?.map((provider) => {
			return typeof provider.manifestsPromise !== 'undefined'
				? provider.manifestsPromise
				: provider;
		});

		Promise.all(providerPromises)
			.then(() => setProvidersReady(true))
			.catch((_error) => {
				// NOOP: Here, if any of the provider promises fail, the best we can do is to
				// fall back the original experience where macros are resolved and rendered on
				// the client only, although in this case the extensions experience would likely
				// be hosed anyway.
				setProvidersReady(true);
			});
	}, [extensionProviders]);

	const extensionHandlers = useMemo(
		() => createEditorSSRExtensionHandlers({ contentId, spaceKey, cloudId, userId }),
		[contentId, spaceKey, cloudId, userId],
	);

	return isTransition || providersReady ? undefined : extensionHandlers;
};
